import React, { useState } from "react";
import api from "../Services/api";
import toast from "react-hot-toast";

export default function CallInteractionModal({ callData, onClose }) {
  const [subStatus, setSubStatus] = useState(callData.sub_status || "");
  const [status, setStatus] = useState("Contacted");
  const [customSubStatus, setCustomSubStatus] = useState("");
  const [note, setNote] = useState("");
  const [setNextFollowup, setSetNextFollowup] = useState(false);
  const [nextFollowupDate, setNextFollowupDate] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const STATUS_OPTIONS = ["New", "Contacted", "Interested", "Form Filled", "Admission Done", "Pipeline", "Lost"];
  const SUB_STATUS_OPTIONS = ["Not Interested", "Ringing / No Answer", "Interested", "Admission Done", "Wrong Number", "course not available", "will think on it", "callback", "Other"];

  const handleSave = async () => {
    if (!subStatus || !note) {
      toast.error("⚠️ Please fill in all mandatory fields");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        sub_status: subStatus === "Other" ? customSubStatus : subStatus,
        note: note,
        next_followup_date: setNextFollowup ? nextFollowupDate : null,
        interaction_type: "Call",
        call_log_id: callData.call_log_id,
        lead_status: status,
        interaction_status: "Completed"
      };

      const res = await api.post(`crm/leads/${callData.lead_id}/log_interaction/`, payload);
      toast.success("✅ Interaction logged successfully");
      window.dispatchEvent(new CustomEvent('crm:interaction_saved', { 
        detail: { lead_id: callData.lead_id, updatedLead: res.data } 
      }));
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "❌ Failed to save log");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="ld-modal-backdrop" style={{ zIndex: 10001 }}>
      <div className="ld-modal" style={{ maxWidth: '500px' }}>
        <div className="ld-modal-head" style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ 
              width: '45px', height: '45px', borderRadius: '12px', 
              background: '#f2f7e4', color: '#799F0C',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '20px'
            }}>
              📞
            </div>
            <div>
              <p className="ld-modal-eyebrow">ACTIVE CALL LOGGING</p>
              <h2 className="ld-modal-title" style={{ fontSize: '1.25rem' }}>{callData.lead_name}</h2>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Call Duration</div>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#799F0C' }}>{callData.duration}s</div>
          </div>
        </div>

        <div className="ld-modal-body" style={{ padding: '20px' }}>
          {/* Metadata Grid */}
          <div style={{ 
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', 
            marginBottom: '20px', padding: '12px', background: '#f8fafc', borderRadius: '10px' 
          }}>
            <div>
              <label style={{ fontSize: '10px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Phone Number</label>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{callData.client_number || callData.lead_phone}</div>
            </div>
            <div>
              <label style={{ fontSize: '10px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Call Status</label>
              <div style={{ fontSize: '13px', fontWeight: 700, color: callData.call_status === 'Connected' ? '#22c55e' : '#ef4444' }}>{callData.call_status}</div>
            </div>
          </div>

          {/* Previous Call Summary Context */}
          <div style={{ marginBottom: '20px' }}>
             <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Last Call Summary</label>
             <div style={{ padding: '12px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400e', marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
                   <span>{callData.previous_sub_status || 'New Lead'}</span>
                   <span style={{ opacity: 0.6 }}>Previous Outcome</span>
                </div>
                <div style={{ fontSize: '12.5px', color: '#b45309', fontWeight: 500, lineHeight: 1.4 }}>
                   "{callData.previous_note || 'No interaction summary available.'}"
                </div>
             </div>
          </div>

          {/* Interaction Inputs */}
          <div className="ld-form-row">
            <div className="ld-form-field">
              <label className="ld-form-label">Lead Status <span className="req">*</span></label>
              <select className="ld-form-input" value={status} onChange={e => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="ld-form-field">
              <label className="ld-form-label">Current Sub Status <span className="req">*</span></label>
              <select className="ld-form-input" value={subStatus} onChange={e => setSubStatus(e.target.value)}>
                <option value="">Select sub-status...</option>
                {SUB_STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          </div>

          <div className="ld-form-row">
            <div className="ld-form-field">
              <label className="ld-form-label">Follow-up Reminder</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '38px' }}>
                <input type="checkbox" checked={setNextFollowup} onChange={e => setSetNextFollowup(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Schedule next?</span>
              </div>
            </div>
          </div>

          {subStatus === "Other" && (
            <div className="ld-form-field" style={{ marginBottom: '15px' }}>
               <input className="ld-form-input" placeholder="Type custom status..." value={customSubStatus} onChange={e => setCustomSubStatus(e.target.value)} />
            </div>
          )}

          {setNextFollowup && (
            <div className="ld-form-field" style={{ marginBottom: '15px' }}>
              <label className="ld-form-label">Next Follow-up Date</label>
              <input type="datetime-local" className="ld-form-input" value={nextFollowupDate} onChange={e => setNextFollowupDate(e.target.value)} />
            </div>
          )}

          <div className="ld-form-field">
            <label className="ld-form-label">Call Summary / Notes <span className="req">*</span></label>
            <textarea 
              className="ld-form-input" 
              rows={3} 
              placeholder="What was discussed?" 
              value={note} 
              onChange={e => setNote(e.target.value)}
              style={{ resize: 'none', padding: '10px' }}
            />
          </div>
        </div>

        <div className="ld-modal-foot">
          <button className="ld-btn-save" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving interaction..." : "Save Interaction & Close"}
          </button>
        </div>
      </div>
    </div>
  );
}



